import { useEffect, useState } from "react";
import {
  ChartPieIcon,
  HomeIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFormEntries,
  setFormEntriesActiveFormName,
} from "../../../store/slices/formEntriesSlice";
import { AppDispatch, RootState } from "../../../store";
import { formMappings } from "../../../config/formMappings";
import { setActiveFormName } from "../../../store/slices/activeFormSlice";
import LoadingComponent from "../../LoadingComponent";
import { Header } from "./Header";
import SideNavigation from "./SideNavigation";
import MobileMenu from "./MobileMenu";

const navigation = [
  { name: "Dashboard", href: "/", icon: HomeIcon },
  {
    name: "All Registrations",
    href: "/all-registrations",
    icon: TableCellsIcon,
  },
  { name: "Reports", href: "/reports", icon: ChartPieIcon },
];

export default function InternalPage(props: { children: any }) {
  const dispatch = useDispatch<AppDispatch>();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pathname = window.location.pathname;

  // Select loading, error, and entries data from the Redux store
  const loadingEntries = useSelector(
    (state: RootState) => state.formEntries.loading
  );
  const errorEntries = useSelector(
    (state: RootState) => state.formEntries.error
  );
  const entries = useSelector((state: RootState) => state.formEntries.entries);
  const activeFormKey = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );

  // Get the formName from the formKey, or fallback to the key itself
  const activeFormName =
    formMappings[activeFormKey as keyof typeof formMappings]?.formName ||
    activeFormKey;

  // Handle form selection - updates Redux state and fetches entries
  const handleFormSelect = (formKey: string) => {
    // If the selected form is already active, do nothing
    if (activeFormKey === formKey) {
      return;
    }

    const formMapping = formMappings[formKey as keyof typeof formMappings];
    if (formMapping) {
      dispatch(setActiveFormName(formKey));
      dispatch(setFormEntriesActiveFormName(formKey));
      // Fetch form entries with the new form ID
      dispatch(fetchFormEntries(formMapping.formId));
    }
  };

  // Get the currently loaded formId from Redux
  const loadedFormId = useSelector(
    (state: RootState) => state.formEntries.loadedFormId
  );
  const hasEntries = useSelector(
    (state: RootState) => state.formEntries.allEntries.length > 0
  );

  // Fetch entries when active form changes or if they haven't been loaded yet
  useEffect(() => {
    const formMapping =
      formMappings[activeFormKey as keyof typeof formMappings];
    if (formMapping) {
      // Only fetch if we don't already have entries for this formId
      if (loadedFormId !== formMapping.formId || !hasEntries) {
        dispatch(setFormEntriesActiveFormName(activeFormKey));
        dispatch(fetchFormEntries(formMapping.formId));
      } else {
        // Just update the active form name if entries are already loaded
        dispatch(setFormEntriesActiveFormName(activeFormKey));
      }
    }
  }, [dispatch, activeFormKey, loadedFormId, hasEntries]);

  // Conditional rendering for error state
  if (errorEntries) return <p>Error: {errorEntries}</p>;

  return (
    <div>
      <MobileMenu
        pathname={pathname}
        navItems={navigation}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeFormName={activeFormName}
        handleFormSelect={handleFormSelect}
      />

      <SideNavigation
        pathname={pathname}
        navItems={navigation}
        activeFormName={activeFormName}
        handleFormSelect={handleFormSelect}
      />

      <div className="lg:pl-72">
        <Header setSidebarOpen={setSidebarOpen} />

        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            {loadingEntries ? <LoadingComponent /> : props.children}
          </div>
        </main>
      </div>
    </div>
  );
}
